import React from 'react';

import { useSystem } from '@core';
import { SidebarPosition } from '@core/types';
import { ThumbnailType } from '@ui/types';
import { noop } from '@utils';
import StudyItem from '../StudyItem';
import ThumbnailList from '../ThumbnailList';

export interface DisplaySet {
	displaySetInstanceUID: string;
	imageSrc?: string;
	imageAltText?: string;
	seriesDate?: string;
	seriesNumber?: string | number;
	numInstances?: number;
	description?: string;
	componentType: ThumbnailType;
	viewportIdentificator?: string[];
	isTracked?: boolean;
	dragData?;
	modality?: string;
	orientation?: string;
	canReject?: boolean;
	onReject?: () => void;
	messages?: any;
	isHydratedForDerivedDisplaySet?: boolean;
	loadingProgress?: number;
	isSubSeries?: boolean;
	isOriginSeries?: boolean;
	SeriesInstanceUID?: string;
}

export interface Study {
	studyInstanceUid: string;
	date?: string;
	numInstances?: string | number;
	modalities?: string;
	description?: string;
	displaySets: DisplaySet[];
	patientName?: string;
	patientBirthDate?: string;
}

export interface Tab {
	name: string;
	label: string;
	studies: Study[];
}

export interface StudyBrowserProps {
	tabs: Tab[];
	activeTabName: string;
	expandedStudyInstanceUIDs: string[];
	activeDisplaySetInstanceUIDs?: string[];
	onClickTab: (tabName: string) => void;
	onClickStudy?: (studyInstanceUid: string) => void;
	onClickThumbnail?: (displaySetInstanceUID: string) => void;
	onDoubleClickThumbnail?: (displaySetInstanceUID: string) => void;
	onClickUntrack?: (displaySetInstanceUID: string) => void;
	onSubPanel?: boolean;
	onMobile?: boolean;
	sidePosition?: SidebarPosition;
}

const getTrackedSeries = (displaySets: DisplaySet[]): number => {
	let trackedSeries = 0;
	displaySets.forEach((displaySet) => {
		if (displaySet.isTracked) {
			trackedSeries++;
		}
	});

	return trackedSeries;
};

const StudyBrowser = ({
	tabs,
	activeTabName,
	expandedStudyInstanceUIDs,
	// onClickTab = noop,
	onClickStudy = noop,
	onClickThumbnail = noop,
	onDoubleClickThumbnail = noop,
	onClickUntrack = noop,
	activeDisplaySetInstanceUIDs,
	onSubPanel = false,
	onMobile = false,
	sidePosition,
}: StudyBrowserProps): React.ReactElement => {
	const { servicesManager } = useSystem();
	// const { t } = useTranslation('StudyBrowser');
	// const { customizationService } = servicesManager?.services || {};
	const getTabContent = () => {
		const tabData = tabs.find((tab) => tab.name === activeTabName);
		return tabData.studies.map(
			({ studyInstanceUid, date, description, numInstances, modalities, displaySets, patientName, patientBirthDate }) => {
				const isExpanded = expandedStudyInstanceUIDs.includes(studyInstanceUid);
				const numSeries = displaySets.length;
				const numStudies = tabData?.studies?.length;

				return (
					<div key={studyInstanceUid} className="flex flex-col gap-0.5" style={{ maxHeight: `calc(100% / ${numStudies})` }}>
						{!onMobile && (
							<StudyItem
								date={date}
								description={description}
								numInstances={numInstances}
								modalities={modalities}
								trackedSeries={getTrackedSeries(displaySets)}
								isActive={isExpanded}
								onClick={() => {
									onClickStudy(studyInstanceUid);
								}}
								patientName={patientName}
								patientBirthDate={patientBirthDate}
								numSeries={numSeries}
								onSubPanel={onSubPanel}
								data-cy="thumbnail-list"
							/>
						)}
						{isExpanded && displaySets && (
							<ThumbnailList
								thumbnails={displaySets}
								activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
								onClickThumbnail={onClickThumbnail}
								onDoubleClickThumbnail={onDoubleClickThumbnail}
								onClickUntrack={onClickUntrack}
								servicesManager={servicesManager}
								onSubPanel={onSubPanel}
								onMobile={onMobile}
								sidePosition={sidePosition}
							/>
						)}
					</div>
				);
			},
		);
	};

	return (
		<React.Fragment>
			{/* <div
				className="w-100 flex h-16 flex-row items-center justify-center border-b border-secondary-light bg-primary-dark p-4"
				data-cy={'studyBrowser-panel'}
			>
				<LegacyButtonGroup
					variant="outlined"
					color="secondary"
					splitBorder={false}
				>
					{tabs.map((tab) => {
						const { name, label, studies } = tab;
						const isActive = activeTabName === name;
						const isDisabled = !studies.length;
						// Apply the contrasting color for brighter button color visibility
						const classStudyBrowser = customizationService?.getModeCustomization('class:StudyBrowser') || {
							true: 'default',
							false: 'default'
						};
						const color = classStudyBrowser[`${isActive}`];
						return (
							<LegacyButton
								key={name}
								className={'min-w-18 p-2 text-base text-white'}
								size="initial"
								color={color}
								bgColor={isActive ? 'bg-primary-main' : 'bg-black'}
								onClick={() => {
									onClickTab(name);
								}}
								disabled={isDisabled}
							>
								{t(label)}
							</LegacyButton>
						);
					})}
				</LegacyButtonGroup>
			</div> */}
			<div className="invisible-scrollbar flex h-full flex-col gap-1 overflow-hidden">{getTabContent()}</div>
		</React.Fragment>
	);
};

export default StudyBrowser;
